import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import AuthControl from "@/components/auth/AuthControl";
import BrandMark from "@/components/BrandMark";
import FooterBottom from "@/components/FooterBottom";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { FooterNav, HeaderNav, MenuNav } from "@/components/navigation/SiteNav";
import Seo from "@/components/Seo";
import { useNavFit } from "@/hooks/useNavFit";

const ShellFooter = () => (
  <footer
    data-testid="shell-footer"
    className="zone-graphite bg-paper relative border-t border-ink/10 overflow-hidden"
  >
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
    <img
      src="/provena-logo.png"
      alt=""
      aria-hidden="true"
      className="pointer-events-none select-none absolute -right-16 -bottom-20 w-64 opacity-[0.04]"
      draggable="false"
    />
    <div className="relative max-w-7xl mx-auto px-5 md:px-12 py-12">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
        <img
          src="/provena-logo-relief.png"
          alt="Provena Registry"
          className="h-16 w-auto object-contain self-start"
        />
        <FooterNav />
      </div>
      <FooterBottom />
    </div>
  </footer>
);

export const PageShell = ({ children, testId, title, description, image, imageAlt, noindex, bareTitle, canonicalPath }) => {
  const [open, setOpen] = useState(false);
  const { barRef, brandRef, navRef, controlsRef, toggleRef, navFits } = useNavFit();

  // Le menu se referme si la barre retrouve la place de tout afficher.
  useEffect(() => {
    if (navFits) setOpen(false);
  }, [navFits]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div
      data-testid={testId}
      className="min-h-screen surface-dial text-ink font-body flex flex-col"
    >
      <Seo title={title} description={description} image={image} imageAlt={imageAlt} noindex={noindex} bare={bareTitle} canonicalPath={canonicalPath} />
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-paper/80 border-b border-ink/10">
        <div
          ref={barRef}
          className="max-w-[1600px] mx-auto px-5 md:px-12 h-20 md:h-24 flex items-center justify-between gap-3"
        >
          <Link to="/" ref={brandRef} data-testid="shell-brand" className="flex items-center shrink-0">
            <BrandMark />
          </Link>

          {/* What does not fit moves to the menu, whole: a too-narrow bar
              never truncates a label (see useNavFit). */}
          <HeaderNav ref={navRef} fits={navFits} />

          <div ref={controlsRef} className="flex items-center gap-3 sm:gap-4 shrink-0">
            <div className="hidden sm:block">
              <AuthControl />
            </div>
            {/* Last in the bar: the language switch belongs at the far edge, not
                wedged between the navigation and the account controls. */}
            <LanguageSwitcher compact />
            {/* Mobile menu toggle */}
            <button
              type="button"
              ref={toggleRef}
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              aria-expanded={open}
              data-testid="shell-menu-toggle"
              className={`${navFits ? "hidden" : "flex"} items-center justify-center h-11 w-11 -mr-2 text-gold-ink`}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Même raison qu'en page d'accueil : le corps est verrouillé pendant
            l'ouverture, donc le menu doit défiler lui-même, sans quoi la
            déconnexion reste hors d'atteinte sur un petit écran. */}
        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="shell-mobile-menu"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="border-t border-ink/10 bg-paper/95 backdrop-blur-xl max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain"
            >
              <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col">
                <MenuNav onNavigate={() => setOpen(false)} />
                <div className="mt-4 pt-4 border-t border-ink/10 sm:hidden">
                  <AuthControl variant="stack" onNavigate={() => setOpen(false)} />
                </div>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
      <main className="flex-1">{children}</main>
      <ShellFooter />
    </div>
  );
};

export default PageShell;
